// @flow

import React, { useState } from 'react';
import cx from 'classnames';
import styles from './CurrencyInput.module.css';

import { Paragraph, ErrorMessage, HelpText } from 'operation-diameter';
import SupportingText from '../SupportingText';

export const formatCurrency = (
  value: string | number,
  { locale = 'en-GB', currency = 'GBP' } = {}
) => {
  try {
    const endsWithDecimal = value.toString().endsWith('.');
    const isDecimalValue = !!value.toString().includes('.');
    const hasDecimalZero = /(\.[0-9]*0)$/.test(value.toString());
    const parsedFloat = parseFloat(`${value.toString().replace(/,/g, '')}`);

    if (isNaN(parsedFloat)) {
      return '';
    }

    const formattedValue = new Intl.NumberFormat(locale, {
      maximumFractionDigits: isDecimalValue ? 2 : 0,
      minimumFractionDigits: hasDecimalZero ? 2 : 0,
      currency
    }).format(parsedFloat);

    if (endsWithDecimal) return `${formattedValue}.`;

    return formattedValue;
  } catch {
    console.error('Error formatting currency');
    return '';
  }
};

export type CurrencyInputProps = {
  /**
   * Text to display
   */
  label: string,
  /**
   * What is the name of the input
   */
  name: string,
  /**
   * What should happen when the input value changes?
   */
  onChange?: (event: React.ChangeEvent<HTMLInputElement>) => void,
  /**
   * What is the initial value of the input?
   */
  value: string,
  /**
   * Additional styles to be applied
   */
  additionalClassnames?: string,
  /**
   * What currency should the input display?
   */
  currency?: 'GBP' | 'USD',
  /**
   * What custom error message should appear below the input?
   */
  error?: string,
  /**
   * Heading on top of the input, it will be used as a label for the input if provided.
   */
  heading?: string,
  /**
   * What text should appear below the input to provide further information?
   */
  helpText?: string,
  /**
   * Should the input be autofocused?
   */
  isAutoFocus?: boolean,
  /**
   * Should the input show disabled state?
   */
  isDisabled?: boolean,
  /**
   * Should text be read only?
   */
  isReadOnly?: boolean,
  /**
   * Is the input required?
   */
  isRequired?: boolean,
  /**
   * What is the maximum value that should be accepted?
   */
  max?: number,
  /**
   * What is the minimum value that should be accepted?
   */
  min?: number,
  /**
   * What should happen when the user removes the focus from the input?
   */
  onBlur?: (event: React.ChangeEvent<HTMLInputElement>) => void,
  /**
   * What should happen when the user focus on the input?
   */
  onFocus?: (event: React.ChangeEvent<HTMLInputElement>) => void,
  /**
   * What variant should be applied?
   */
  variant?: 'light' | 'coloured'
};

/**
 * ## Overview
 * Input is used to create interactive controls for forms in order to accept
 * data from the user. This component is specifically for the currency inputs.
 *
 * It has all the features of a standard input: it can be autofocused, disabled, read-only, required, and can have a label, a heading,
 * some help text and a custom error.
 *
 * The key features of the CurrencyInput component are:
 * - **Currency prefix**: The currency prefix is displayed before the input field. We only support £ and $ for now.
 * - **Value formatting**: The value is filtered and formatted to display commas and decimal points.
 * - **Error handling**: The component can display errors if the value is not compliant with the `min` and max `values`,
 * or can display a custom `error`.
 *
 * In terms of styling, it can have a `light` or `coloured` variant, and it is default to the `coloured` one.
 * The `light` variant is used on coloured backgrounds and the `coloured` variant is used on light backgrounds.
 * You can personalise the component with `additionalClassnames`.
 */

const CurrencyInput = ({
  label,
  name,
  onChange,
  value,
  additionalClassnames,
  currency = 'GBP',
  error: externalError = null,
  heading,
  helpText,
  isAutoFocus = false,
  isDisabled = false,
  isReadOnly = false,
  isRequired = false,
  max,
  min,
  onBlur,
  onFocus,
  variant = 'coloured',
  ...props
}: CurrencyInputProps) => {
  // Transform the string into a number, without special characters (except the period for the decimal point)
  const sanitizeNumericString = (value: string) => value.replace(/[^0-9]/g, '');

  const formattedValue = () => {
    const numericValue = sanitizeNumericString(value);
    const formatted_value = formatCurrency(numericValue, { currency });
    return formatted_value;
  };
  const [internalValue, setInternalValue] = useState<string>(formattedValue());
  const [internalError, setInternalError] = useState<string | null>(null);

  const prefix = '£';

  const handleInvalid = (e: React.ChangeEvent<HTMLInputElement>) => {
    // Triggered by checkedValidity() if input is invalid
    const target = e.target;
    setInternalError(target.validationMessage);
  };

  const handleBlur = (e: React.ChangeEvent<HTMLInputElement>) => {
    const target = e.target;
    target.checkValidity();

    const numericValue = parseFloat(sanitizeNumericString(internalValue));

    if (min !== undefined && numericValue < min) {
      setInternalError(
        `Value must be greater than or equal to ${prefix}${formatCurrency(min, {
          currency
        })}`
      );
    } else if (max !== undefined && numericValue > max) {
      setInternalError(
        `Value must be less than or equal to ${prefix}${formatCurrency(max, {
          currency
        })}`
      );
    } else if (!target.validity.valid) {
      setInternalError(target.validationMessage);
    } else {
      setInternalError(null);
    }

    // Format the value with the currency symbol
    const formatted_value = formatCurrency(numericValue, { currency });
    setInternalValue(formatted_value);
    e.target.value = formatted_value.replace(/,/g, '');
    onBlur?.(e);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    // Update the internal value on every change without formatting
    // Formatting is happening onBlur
    const value = e.target.value;
    const formatted_value =
      typeof value === 'string' && value !== ''
        ? sanitizeNumericString(value)
        : value;
    setInternalValue(formatted_value);
    onChange?.(e);
  };

  const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
    const numericValue = parseFloat(sanitizeNumericString(internalValue));
    setInternalValue(numericValue ? numericValue.toString() : '');
    onFocus?.(e);
  };

  // Determine the error message to display, prioritizing external error
  const errorMessage = externalError || internalError;
  const helpTextId = (helpText && `${name}-help-text`) || null;
  const errorMessageId = (errorMessage && `${name}-error-message`) || null;

  return (
    <div
      data-testid='inputWrapper'
      className={cx(styles.wrapper, styles[variant], additionalClassnames, {
        [styles.error]: !!errorMessage
      })}>
      {heading && <Paragraph>{heading}</Paragraph>}
      <div className={styles.controlWrapper}>
        <label htmlFor={name} className={styles.label}>
          {label}
        </label>
        <input
          id={name}
          data-testid='currency-input'
          name={name}
          value={internalValue}
          onChange={handleChange}
          onBlur={handleBlur}
          onFocus={handleFocus}
          onInvalid={handleInvalid}
          required={isRequired}
          disabled={isDisabled}
          autoFocus={isAutoFocus}
          readOnly={isReadOnly}
          aria-invalid={!!errorMessage}
          aria-errormessage={errorMessageId}
          aria-describedby={errorMessageId || helpTextId}
          aria-label={`${heading || label}. Currency used is £`}
          autoComplete='off'
          placeholder={label}
          className={cx(styles.input, { [styles.populated]: !!internalValue })}
          min={min}
          max={max}
          {...props}
        />
        <Paragraph
          paragraphSize='p1'
          additionalClassnames={styles.prefix}
          aria-hidden={true}>
          {prefix}
        </Paragraph>
      </div>
      {(errorMessage || (!errorMessage && helpText)) && (
        <SupportingText>
          {errorMessage && (
            <ErrorMessage message={errorMessage} id={errorMessageId} />
          )}
          {!errorMessage && !!helpText.trim().length && (
            <HelpText text={helpText} id={helpTextId} />
          )}
        </SupportingText>
      )}
    </div>
  );
};

export default CurrencyInput;
